import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, Pause, Play, RotateCcw, X, PackageOpen, FastForward, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import Layout from "../components/layout/Layout";
import AuthGuard from "../components/AuthGuard";
import EmptyState from "../components/EmptyState";
import { api } from "../api/client";

const inr = (n) => `₹${Number(n || 0).toFixed(2)}`;
const fmtDate = (s) => s ? new Date(s + "T00:00:00").toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const STATUS_CHIP = {
    active: "bg-emerald-50 text-emerald-700 ring-emerald-100",
    paused: "bg-amber-50 text-amber-700 ring-amber-100",
    cancelled: "bg-zinc-100 text-zinc-500 ring-zinc-200",
};

const Card = ({ sub, onAction, busy }) => {
    const p = sub.product;
    return (
        <article
            className="group relative overflow-hidden rounded-3xl border border-zinc-100 bg-white p-5 shadow-soft transition-all hover:-translate-y-0.5 hover:border-brand-200 hover:shadow-brand"
            data-testid={`subscription-card-${sub.id}`}
        >
            <div className="flex gap-4">
                <Link to={p ? `/products/${p.id}` : "#"} className="shrink-0">
                    <img
                        src={p?.image || "/pills-logo.jpg"}
                        alt={p?.name || "Product"}
                        className="h-20 w-20 rounded-2xl object-cover ring-1 ring-zinc-200"
                    />
                </Link>
                <div className="min-w-0 flex-1">
                    <div className="flex items-start gap-2">
                        <h3 className="line-clamp-1 text-sm font-semibold text-zinc-900">{p?.name || "Subscription"}</h3>
                        <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ring-1 ${STATUS_CHIP[sub.status]}`} data-testid={`sub-status-${sub.id}`}>
                            {sub.status}
                        </span>
                    </div>
                    <p className="mt-0.5 line-clamp-1 text-xs text-zinc-500">
                        {p?.brand ? `${p.brand} • ` : ""}{p?.pack_size || "1 pack"} • Qty {sub.qty}
                    </p>
                    <div className="mt-2 flex items-center gap-2 text-xs text-zinc-600">
                        <CalendarClock className="h-3.5 w-3.5 text-brand-700" />
                        <span>
                            {sub.status === "cancelled" ? "Cancelled" : sub.status === "paused" ? "Paused" : "Next refill"}
                            {sub.status === "active" && <strong className="ml-1 text-zinc-900">{fmtDate(sub.next_refill_date)}</strong>}
                        </span>
                    </div>
                    {sub.prescription && (
                        <p className="mt-1 inline-flex items-center gap-1 text-[11px] text-brand-700">
                            <ShieldCheck className="h-3 w-3" /> Rx attached
                        </p>
                    )}
                </div>
                <div className="text-right">
                    <p className="text-sm font-semibold text-zinc-900">{inr((sub.price_at_creation || 0) * (sub.qty || 1))}</p>
                    <p className="text-[10px] uppercase tracking-wide text-zinc-400">per refill</p>
                </div>
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-2 border-t border-zinc-100 pt-4">
                <Link
                    to={`/subscriptions/${sub.id}`}
                    className="rounded-full border border-zinc-200 px-3 py-1.5 text-xs font-medium text-zinc-700 hover:border-brand-300 hover:text-brand-700"
                    data-testid={`sub-view-${sub.id}`}
                >
                    View
                </Link>

                {sub.status === "active" && (
                    <>
                        <button
                            disabled={busy}
                            onClick={() => onAction(sub, "refill-now")}
                            className="inline-flex items-center gap-1.5 rounded-full bg-brand-700 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-brand-600 disabled:opacity-50"
                            data-testid={`sub-refill-${sub.id}`}
                        >
                            <RotateCcw className="h-3.5 w-3.5" /> Refill now
                        </button>
                        <button
                            disabled={busy}
                            onClick={() => onAction(sub, "skip")}
                            className="inline-flex items-center gap-1.5 rounded-full border border-zinc-200 px-3 py-1.5 text-xs font-medium text-zinc-700 hover:border-brand-300 hover:text-brand-700 disabled:opacity-50"
                            data-testid={`sub-skip-${sub.id}`}
                        >
                            <FastForward className="h-3.5 w-3.5" /> Skip
                        </button>
                        <button
                            disabled={busy}
                            onClick={() => onAction(sub, "pause")}
                            className="inline-flex items-center gap-1.5 rounded-full border border-zinc-200 px-3 py-1.5 text-xs font-medium text-zinc-700 hover:border-brand-300 hover:text-brand-700 disabled:opacity-50"
                            data-testid={`sub-pause-${sub.id}`}
                        >
                            <Pause className="h-3.5 w-3.5" /> Pause
                        </button>
                    </>
                )}
                {sub.status === "paused" && (
                    <button
                        disabled={busy}
                        onClick={() => onAction(sub, "resume")}
                        className="inline-flex items-center gap-1.5 rounded-full bg-brand-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-600 disabled:opacity-50"
                        data-testid={`sub-resume-${sub.id}`}
                    >
                        <Play className="h-3.5 w-3.5" /> Resume
                    </button>
                )}
                {sub.status !== "cancelled" && (
                    <button
                        disabled={busy}
                        onClick={() => onAction(sub, "cancel")}
                        className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-rose-100 px-3 py-1.5 text-xs font-medium text-rose-600 hover:bg-rose-50 disabled:opacity-50"
                        data-testid={`sub-cancel-${sub.id}`}
                    >
                        <X className="h-3.5 w-3.5" /> Cancel
                    </button>
                )}
            </div>
        </article>
    );
};

const SubscriptionsContent = () => {
    const [subs, setSubs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");
    const [busyId, setBusyId] = useState(null);

    const load = async () => {
        try {
            setLoading(true);
            const params = {};
            if (filter !== "all") params.status = filter;
            const { data } = await api.get("/subscriptions", { params });
            setSubs(data);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [filter]); // eslint-disable-line

    const handleAction = async (sub, action) => {
        if (action === "cancel" && !window.confirm("Cancel this subscription? This cannot be undone.")) return;
        setBusyId(sub.id);
        try {
            if (action === "refill-now") {
                const { data } = await api.post(`/subscriptions/${sub.id}/refill-now`);
                toast.success(`Order ${data.order_no} created — opening payment...`);
                setTimeout(() => { window.location.href = data.redirect_url; }, 700);
                return;
            }
            await api.post(`/subscriptions/${sub.id}/${action}`);
            toast.success(`Subscription ${action}d`);
            await load();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const msg = typeof detail === "string" ? detail : detail?.message || `${action} failed`;
            toast.error(msg);
        } finally {
            setBusyId(null);
        }
    };

    return (
        <div data-testid="subscriptions-page">
            <div className="mb-6 flex flex-wrap items-center gap-2">
                {["all", "active", "paused", "cancelled"].map((f) => (
                    <button
                        key={f}
                        onClick={() => setFilter(f)}
                        className={`rounded-full px-4 py-1.5 text-xs font-medium capitalize transition-colors ${
                            filter === f
                                ? "bg-brand-700 text-white shadow-sm"
                                : "border border-zinc-200 text-zinc-700 hover:border-brand-300 hover:text-brand-700"
                        }`}
                        data-testid={`sub-filter-${f}`}
                    >
                        {f}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="grid gap-4 sm:grid-cols-2">
                    {[0,1,2,3].map((i) => <div key={i} className="h-48 skeleton rounded-3xl" />)}
                </div>
            ) : subs.length === 0 ? (
                <EmptyState
                    icon={<PackageOpen className="h-7 w-7" />}
                    title={filter === "all" ? "No subscriptions yet" : `No ${filter} subscriptions`}
                    description="Subscribe to your regular medicines and we'll remind you when it's time to refill — never miss a dose."
                    action={<Link to="/products" className="btn-brand">Browse products</Link>}
                />
            ) : (
                <div className="grid gap-4 sm:grid-cols-2">
                    {subs.map((s) => (
                        <Card key={s.id} sub={s} onAction={handleAction} busy={busyId === s.id} />
                    ))}
                </div>
            )}
        </div>
    );
};

const Subscriptions = () => (
    <Layout>
        <section className="mx-auto max-w-7xl px-6 py-10 lg:px-12">
            <header className="mb-8">
                <p className="label-uppercase">Account</p>
                <h1 className="mt-1 text-3xl font-semibold tracking-tight text-zinc-900 sm:text-4xl">Subscriptions</h1>
                <p className="mt-2 max-w-2xl text-sm text-zinc-500">
                    Monthly auto-refill reminders for your essentials. We'll ping you when it's time — you stay in control of every order.
                </p>
            </header>
            <AuthGuard>
                <SubscriptionsContent />
            </AuthGuard>
        </section>
    </Layout>
);

export default Subscriptions;
